import { ButtonHTMLAttributes, DetailedHTMLProps, HTMLAttributeAnchorTarget, ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';

import CircularProgressSpin from '@/components/CircularProgressSpin';
import CustomNextLink from '@/components/CustomNextLink';
import LoadingSpinner from '@/components/LoadingSpinner';
import ArrowRightIcon from '@/icons/arrowRight';
import { TChildrenProps } from '@/models/components';

interface ICustomButtonProps
	extends TChildrenProps,
		TClassNameProps,
		DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
	showStartAdornmentIcon?: boolean;
	showEndAdornmentIcon?: boolean;
	startAdornmentIcon?: JSX.Element;
	endAdornmentIcon?: JSX.Element;
	loadingIcon?: JSX.Element;
	showLoadingIndicator?: boolean;
	isLoading?: boolean;
	bordered?: boolean;
}

/** For newer integration of button, kindly use this instead of the CustomedMui/CustomButton as we are migrating
 * MUI to tailwind. Temporarily naming CustomTButton on the folder just to state it's a tailwind component
 */

const CustomButton = ({
	showStartAdornmentIcon = false,
	showEndAdornmentIcon = true,
	loadingIcon = <CircularProgressSpin />,
	bordered = false,
	showLoadingIndicator = false,
	isLoading = false,
	startAdornmentIcon,
	endAdornmentIcon = (
		<ArrowRightIcon className="origin h-4 w-6 -translate-x-2 group-hover:translate-x-0 group-hover:transition-transform" />
	),
	children,
	className,
	...props
}: ICustomButtonProps): ReactElement => (
	<button
		role="button"
		className={twMerge(
			`btn-gradient group flex transform items-center justify-between tracking-wider text-white transition-all duration-200 ease-in hover:bg-white-hover`,
			bordered ? 'btn-bordered' : '',
			isLoading ? 'disabled:pointer-events-none' : '',
			className
		)}
		{...props}>
		{showStartAdornmentIcon && startAdornmentIcon}
		{children}
		{showEndAdornmentIcon && !isLoading && endAdornmentIcon}
		{showLoadingIndicator && isLoading && loadingIcon}
	</button>
);

const CUSTOM_BUTTON_LINK_CLASS = 'btn-medium link-btn';

/**
 * Used for showing links in the form of button layout. This is primarily used for login logout
 * By default, it uses bg-white and gray hover bg on light and dark mode
 */
const CustomButtonLink = ({
	loading = false,
	href,
	className,
	children,
	useNextLink,
	target,
	shallow,
}: {
	href: string;
	loading?: boolean;
	useNextLink?: boolean;
	target?: HTMLAttributeAnchorTarget;
	shallow?: boolean;
} & TChildrenProps &
	TClassNameProps): ReactElement =>
	useNextLink ? (
		<CustomNextLink
			withUnderlineAnimation={false}
			role="button"
			shallow={shallow}
			href={href}
			className={`flex ${CUSTOM_BUTTON_LINK_CLASS} ${className}`}>
			{loading && <LoadingSpinner showText={false} svgClassName="aspect-ratio !w-4" />}
			{children}
		</CustomNextLink>
	) : (
		<a role="button" target={target} href={href} className={`${CUSTOM_BUTTON_LINK_CLASS} ${className}`}>
			{loading && <LoadingSpinner showText={false} svgClassName="aspect-ratio !w-4" />}
			{children}
		</a>
	);

export { CustomButton, CustomButtonLink };
